import React, { useMemo } from 'react';
import styled from 'styled-components';
import { device } from '@src/common/breakpoints';
import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { completeCartSelector } from '@src/features/cart/store/cart';
import { BodyLargeBold, BodyRegularMedium, BodyRegularSemiBold } from '@src/fonts/typography/Typography';
import GradientButton from '@src/components/reusable/button/GradientButton';
import CartShadowIcon from '@src/icons/cart-shadow-icon.svg';
import CartItemContainer from './CartItemContainer';
import { useWidgetsTheme as theme } from '@src/core/theme';
import useTranslate from '@src/i18n/useTranslate';
import { changeLocation } from '@src/core/store';
import SpinnerBar from '@src/components/reusable/spinner/SpinnerBar';
import CartPackageItemContainer from './CartItemContainer/CartPackageItemContainer';
import { groupCartItemsByPackageUnitId } from '@src/domains/cart';

type CartDetailsBodyProps = {
  editable?: boolean;
  showBackButtonIfEmpty?: boolean;
  showEmptyInstruction?: boolean;
  showErrors?: boolean;
};

const CartDetailsBody = (props: CartDetailsBodyProps) => {
  const { editable = true, showBackButtonIfEmpty = true, showEmptyInstruction, showErrors = true } = props;

  const { cart, isLoading } = useAppSelector(completeCartSelector);
  const { i18n } = useTranslate();
  const quantity = cart?.items.length ?? 0;
  const checkoutPage = useWidgetParam('landing-page');
  const dispatch = useAppDispatch();
  const cartIsIncomplete = isLoading || !quantity;
  const redirect = () => dispatch(changeLocation(checkoutPage || ''));

  const { packageGroup, individualItems } = useMemo(
    () => groupCartItemsByPackageUnitId(cart?.items ?? []),
    [cart?.items],
  );

  return (
    <CartDetailsBodyStyles>
      <QuantityHeader>
        {i18n('cart_itens')} ({quantity})
      </QuantityHeader>
      <ScrollableBox>
        <CartDetailsItems visible={!cartIsIncomplete}>
          {packageGroup.map((packageItem) => (
            <CartPackageItemContainer
              key={packageItem.productPackage.id}
              items={packageItem.items}
              packageItem={packageItem.productPackage}
              editable={editable}
              showErrors={showErrors}
            />
          ))}

          {individualItems.map((item) => (
            <CartItemContainer key={item.id} item={item} editable={editable} showErrors={showErrors} />
          ))}
        </CartDetailsItems>
      </ScrollableBox>

      <EmptyCartArea visible={cartIsIncomplete}>
        {isLoading ? <SpinnerBar /> : <CartShadowIcon />}
        <CartTitleNotice>{isLoading ? i18n('cart_loading') : i18n('cart_yourCartIsEmpty')}</CartTitleNotice>

        {showEmptyInstruction ? (
          <CartEmptyText>{isLoading ? i18n('cart_pleaseWait') : i18n('cart_addProductInstruction')}</CartEmptyText>
        ) : null}

        {showBackButtonIfEmpty ? (
          <GradientButton onClick={redirect} actionColor={theme('actionColor')}>
            {i18n('cart_backToHome')}
          </GradientButton>
        ) : null}
      </EmptyCartArea>
    </CartDetailsBodyStyles>
  );
};

const CartDetailsBodyStyles = styled.section`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  flex-grow: 1;
  overflow: hidden;
`;

const ScrollableBox = styled.div`
  overflow: auto;
  &::-webkit-scrollbar {
    width: 11px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cdcdcd;
    border-radius: 10px;
  }
`;
const CartTitleNotice = styled.p`
  ${BodyLargeBold}
  && {
    margin-top: 16px;
    margin-bottom: 8px;
  }
`;
const CartEmptyText = styled.p`
  ${BodyRegularMedium}
  && {
    margin-bottom: 32px;
  }
`;
const EmptyCartArea = styled.div<VisibleAreaProps>`
  display: ${(props) => (props.visible ? 'flex' : 'none')};
  flex-direction: column;
  width: 250px;
  align-items: center;
  text-align: center;
  margin: 25vh auto 0 auto;
`;
interface VisibleAreaProps {
  visible: boolean;
}
const CartDetailsItems = styled.div<VisibleAreaProps>`
  display: ${(props) => (props.visible ? 'flex' : 'none')};
  flex-direction: column;
  gap: 24px;
`;

const QuantityHeader = styled.p`
  ${BodyRegularSemiBold}
  && {
    margin-bottom: 16px;
  }
  ${device.mobile} {
    ${BodyLargeBold}
  }
`;
export default CartDetailsBody;
